/**
 * Mis plantillas y el editor: reglas de pantalla de «Nueva plantilla» y «Diseñar con IA» (window.FDPlantillasIA; spec
 * docs/superpowers/specs/2026-10-11-plantillas-clasica-personalizada-ia-design.md §3). Espejo de
 * application/libraries/Plantilla_ia.php (petición y archivo) y de v2/Plantillas (códigos de respuesta); el servidor
 * siempre vuelve a validar. Funciones puras (Vitest en entorno node, sin DOM).
 */

export const MAX_PETICION = 2000;
export const MAX_ARCHIVO = 5 * 1024 * 1024;
export const TIPOS_ARCHIVO = ['image/jpeg', 'image/png', 'application/pdf'];
export const EXTENSIONES_ARCHIVO = ['jpg', 'jpeg', 'png', 'pdf'];
/** Topes del servidor (espejo de Plantilla_ia_model::LIMITE_GRATIS y ::LIMITE_DIA_PREMIUM; plantillas-ia.test.js los compara). */
export const LIMITE_GRATIS = 3;
export const LIMITE_DIA_PREMIUM = 50;
/** Segundos que el servidor le da a un ajuste completo (espejo de Plantilla_ia::PRESUPUESTO_S). */
export const PRESUPUESTO_S = 200;

/** Ejemplos que se pueden tocar debajo de la caja de texto (spec §3.2). */
export const EJEMPLOS = [
    'Logo grande a la izquierda y azul marino',
    'Estilo minimalista en blanco y negro',
    'Recuadros redondeados y mis redes sociales al pie',
];

/**
 * Mensajes serenos mientras la IA trabaja (1-2 minutos, con tope de PRESUPUESTO_S), por segundos transcurridos.
 * `conLogo` es la variante para una cuenta que sí tiene logo: sin él no se promete uno. «Ya casi» solo cerca del tope, y
 * pasado el tope el mensaje deja de prometer.
 */
export const MENSAJES_AVANCE = [
    { desde: 0, texto: 'Estamos leyendo lo que nos pediste…' },
    { desde: 8, texto: 'Estamos acomodando cada parte de tu factura…', conLogo: 'Estamos acomodando tu logo…' },
    { desde: 20, texto: 'Estamos eligiendo colores y tipografía…' },
    { desde: 35, texto: 'Revisando que tu factura conserve todos sus datos fiscales…' },
    { desde: 55, texto: 'Preparando la vista previa…' },
    { desde: 80, texto: 'Esto puede tomar uno o dos minutos; seguimos trabajando en tu diseño.' },
    { desde: 120, texto: 'Está tardando un poco más de lo habitual, pero seguimos en ello. Gracias por esperar.' },
    { desde: PRESUPUESTO_S - 30, texto: 'Ya casi: estamos dando los últimos toques. Gracias por esperar.' },
    { desde: PRESUPUESTO_S + 30, texto: 'Está tardando más de lo habitual. Puedes esperar un poco más o volver a abrir esta página: si el diseño alcanzó a quedar, lo verás en tu borrador.' },
];

export const MSG_CONEXION = 'Perdimos la conexión un momento. Revisa tu internet y vuelve a abrir esta página: si el diseño alcanzó a quedar, lo verás en tu borrador.';
export const MSG_SIN_DISENO = 'No logramos ese diseño esta vez; intenta describirlo con otras palabras. Este intento no se contó.';
/** Gratis sin ajustes: lo que Premium de verdad incluye (el uso justo, su tope diario), nunca «sin límite». */
export const MSG_CUPO_GRATIS_AGOTADO = `Ya usaste tus ${LIMITE_GRATIS} diseños con IA gratis. Con Premium sigues, hasta ${LIMITE_DIA_PREMIUM} ajustes al día.`;
/** Premium con el tope del día usado. */
export const MSG_TOPE_DIA = 'Por hoy ya usaste tus ajustes con IA. Mañana puedes seguir; tu diseño queda guardado.';

/**
 * Otro cambio de la cuenta tiene el candado de las plantillas (un ajuste en otra pestaña, «Volver a esta versión» o un
 * publicar): nunca se afirma cuál. Espejo de Plantillas::MSG_IA_TRABAJANDO (plantillas-ia.test.js los compara).
 */
export const MSG_OTRO_CAMBIO = 'Estamos terminando otro cambio en tus plantillas; inténtalo de nuevo en un momento.';
/** 4034: los 20 intentos sin cobrar del día (espejo de Plantillas::_respuesta_sin_cupo). */
export const MSG_TOPE_INTENTOS = 'Por hoy ya hicimos varios intentos con tu diseño; vuelve mañana y, si quieres, ajusta la petición. Tu plantilla sigue como estaba y este intento no se contó.';
/** Publicar sin respuesta: no se sabe si quedó, así que se dice cómo comprobarlo. */
export const MSG_PUBLICAR_CONEXION = 'Perdimos la conexión un momento. Vuelve a abrir esta página para ver si tu plantilla quedó publicada; tu borrador sigue guardado.';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** El mensaje que toca a los `segundos` de espera. `opciones.logo` = true si la cuenta tiene logo (si no, texto neutro). */
export function mensajeAvance(segundos, opciones) {
    const s = Number(segundos) || 0;
    const conLogo = !!opciones && opciones.logo === true;
    let paso = MENSAJES_AVANCE[0];
    MENSAJES_AVANCE.forEach((m) => { if (s >= m.desde) paso = m; });
    return conLogo && paso.conLogo ? paso.conLogo : paso.texto;
}

/**
 * Espejo de Plantilla_ia::normalizar_peticion: sin caracteres de control, invisibles (ancho cero, controles bidi) ni emojis (4 bytes), espacios y saltos
 * normalizados, 2,000 caracteres. Tras quitar los pares completos, cualquier mitad que quede (un emoji partido al
 * pegar o recortar) está suelta y también se quita (sin lookbehind: Safari 14).
 */
export function normalizarPeticion(texto) {
    let t = String(texto == null ? '' : texto)
        .replace(/[\u{10000}-\u{10FFFF}]/gu, '')
        .replace(/[\uD800-\uDFFF]/g, '')
        .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g, '')
        .replace(/\r\n?/g, '\n');
    t = t.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
    t = t.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
    return Array.from(t).slice(0, MAX_PETICION).join('');
}

/** '' si se puede pedir el diseño; si no, por qué (con calma y con salida). */
export function validarPeticion(texto, hayArchivo) {
    if (normalizarPeticion(texto) === '' && !hayArchivo) {
        return 'Cuéntanos cómo quieres tu factura o sube una foto de la que usabas antes, y la diseñamos.';
    }
    return '';
}

/** '' si el archivo sirve (o no hay); si no, por qué. `archivo` = {name, size, type} (un File) o null. */
export function validarArchivo(archivo) {
    if (!archivo) return '';
    // Algunos sistemas le ponen «application/octet-stream» a un PDF o una foto: se decide por la extensión (el servidor valida el contenido).
    const tipoCrudo = String(archivo.type || '').toLowerCase();
    const tipo = tipoCrudo === 'application/octet-stream' ? '' : tipoCrudo;
    const nombre = String(archivo.name || '').toLowerCase();
    const ext = nombre.includes('.') ? nombre.split('.').pop() : '';
    const tipoOk = TIPOS_ARCHIVO.includes(tipo) || (tipo === '' && EXTENSIONES_ARCHIVO.includes(ext));
    if (!tipoOk) return 'Sube una foto JPG o PNG, o un PDF de tu factura anterior.';
    const size = Number(archivo.size) || 0;
    if (size <= 0) return 'Tu archivo llegó vacío. Vuelve a elegirlo, por favor.';
    if (size > MAX_ARCHIVO) return 'Para subirlo, el archivo puede pesar hasta 5 MB: prueba con una foto más ligera o una captura de pantalla.';
    return '';
}

/**
 * Contador sereno (spec §3.2). `cupo` = {premium, limite, restantes, mostrar} de v2/plantillas/ia_estado_ajax.
 * Gratis: siempre. Premium: solo cuando el servidor dice `mostrar` (le quedan 10 o menos en el día). '' = no se pinta.
 */
export function textoCupo(cupo) {
    if (!cupo || typeof cupo !== 'object') return '';
    const r = Math.max(0, Number(cupo.restantes) || 0);
    if (cupo.premium) {
        if (!cupo.mostrar) return '';
        if (r === 0) return MSG_TOPE_DIA;
        return r === 1 ? 'Hoy te queda 1 ajuste con IA.' : `Hoy te quedan ${r} ajustes con IA.`;
    }
    if (r === 0) return `Ya usaste tus ${Number(cupo.limite) || LIMITE_GRATIS} diseños con IA gratis. Con Premium sigues, hasta ${LIMITE_DIA_PREMIUM} ajustes al día.`;
    return r === 1 ? 'Te queda 1 diseño con IA gratis.' : `Te quedan ${r} diseños con IA gratis.`;
}

/**
 * Qué hacer con la respuesta de disenar_ia_ajax: {tipo: 'ok'|'upsell'|'aviso', msg, gancho?}. null = sin respuesta.
 * El servidor manda su `msg`; si no llega, cada código dice lo que de verdad pasó.
 */
export function respuestaDiseno(r) {
    if (!r || typeof r !== 'object') return { tipo: 'aviso', msg: MSG_CONEXION };
    const code = String(r.code == null ? '' : r.code);
    const msg = String(r.msg == null ? '' : r.msg).trim();
    if (code === '200') {
        const resumen = String(r.resumen || '').trim();
        return { tipo: 'ok', msg: resumen !== '' ? `Listo. ${resumen}` : 'Listo, tu factura ya tiene su nuevo diseño en el borrador.' };
    }
    if (code === '4032') {
        if (Number(r.upsell) === 1) return { tipo: 'upsell', msg: msg || MSG_CUPO_GRATIS_AGOTADO, gancho: String(r.gancho || 'plantilla-ia-ajustes') };
        return { tipo: 'aviso', msg: msg || MSG_CUPO_GRATIS_AGOTADO };
    }
    if (code === '4033') return { tipo: 'aviso', msg: msg || MSG_TOPE_DIA };
    if (code === '4034') return { tipo: 'aviso', msg: msg || MSG_TOPE_INTENTOS };
    if (code === '429') return { tipo: 'aviso', msg: msg || `${MSG_OTRO_CAMBIO} Tu diseño sigue como estaba y este intento no se contó.` };
    return { tipo: 'aviso', msg: msg || MSG_SIN_DISENO };
}

/** El cupo que trae una respuesta, o null si no viene (un 200 sin `cupo` deja el contador como estaba). */
export function cupoDeRespuesta(r) {
    return r && r.cupo && typeof r.cupo === 'object' ? r.cupo : null;
}

/**
 * El `diseno_base` que manda el autosave del editor: la tarjeta marcada, o nada (undefined: el servidor conserva el
 * diseño vigente). Mientras la IA trabaja no se manda nunca: el diseño que está por llegar no se pisa con la tarjeta que
 * seguía marcada al cargar (revisión de la Task 10, [RF-4]).
 */
export function disenoParaGuardar(seleccion, iaTrabajando) {
    if (iaTrabajando) return undefined;
    const s = String(seleccion == null ? '' : seleccion);
    return s === '' ? undefined : s;
}

/** Qué decir tras «Volver a esta versión» (restaurar_version_ajax): {tipo: 'ok'|'aviso', msg}. */
export function respuestaRestaurar(r) {
    if (!r || typeof r !== 'object') return { tipo: 'aviso', msg: MSG_CONEXION };
    const code = String(r.code == null ? '' : r.code);
    const msg = String(r.msg == null ? '' : r.msg).trim();
    if (code === '200') return { tipo: 'ok', msg: msg || 'Listo, volviste a esa versión. Ya la ves en la vista previa.' };
    if (code === '429') return { tipo: 'aviso', msg: msg || `${MSG_OTRO_CAMBIO} Tu borrador sigue como estaba.` };
    return { tipo: 'aviso', msg: msg || 'Tu borrador sigue como estaba. Inténtalo de nuevo en un momento.' };
}

/**
 * Qué decir tras Publicar o Activar (publicar_ajax): {tipo: 'ok'|'revisar'|'aviso', msg}. 'revisar' (4090: el borrador
 * cambió; 5043: el diseño no pasa la revisión de los datos fiscales) pide algo a la persona y va en una ventana; 'aviso'
 * (429, 5002 y lo demás) es pasajero. `activar` = la plantilla aún no estaba activa.
 */
export function respuestaPublicar(r, activar) {
    if (!r || typeof r !== 'object') return { tipo: 'aviso', msg: MSG_PUBLICAR_CONEXION };
    const code = String(r.code == null ? '' : r.code);
    const msg = String(r.msg == null ? '' : r.msg).trim();
    const sigue = activar ? 'Tu plantilla sigue sin activar.' : 'Tu plantilla publicada sigue igual.';
    if (code === '200') {
        return { tipo: 'ok', msg: activar ? 'Plantilla activada. Ya está disponible para tus CFDIs.' : 'Cambios publicados. Ya están en tus facturas.' };
    }
    if (code === '4090') {
        return { tipo: 'revisar', msg: msg || 'Tu borrador cambió mientras lo revisábamos y aún no lo publicamos. Vuelve a publicarlo para incluir lo último.' };
    }
    if (code === '5043') {
        return { tipo: 'revisar', msg: msg || `Antes de publicar revisamos que tu factura muestre todos sus datos fiscales completos y legibles, y con el diseño de ahora alguno no pasa esa revisión. Ajusta los colores, vuelve a una versión anterior o pide un ajuste nuevo. ${sigue}` };
    }
    if (code === '429') return { tipo: 'aviso', msg: msg || `${MSG_OTRO_CAMBIO} ${sigue}` };
    return { tipo: 'aviso', msg: msg || `${sigue} Inténtalo de nuevo en un momento.` };
}

/** '2026-10-11 18:42:05' → '11 oct, 18:42' ('' si no se entiende). */
export function fechaCorta(fecha) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(fecha || ''));
    if (!m) return '';
    const mes = MESES[Number(m[2]) - 1];
    return mes ? `${Number(m[3])} ${mes}, ${m[4]}:${m[5]}` : '';
}

/** Lista de «Versiones» (las 20 que manda el servidor, la más nueva primero) → lo que se pinta. */
export function filasVersiones(versiones) {
    return (Array.isArray(versiones) ? versiones : []).map((v) => {
        const peticion = String(v.peticion || '').replace(/\s+/g, ' ').trim();
        const corta = Array.from(peticion).length > 90 ? `${Array.from(peticion).slice(0, 89).join('')}…` : peticion;
        let detalle = corta;
        if (v.origen === 'restaurada') detalle = 'Volviste a una versión anterior';
        else if (detalle === '') detalle = String(v.resumen || '').trim();
        return { id: Number(v.id), titulo: `Versión ${Number(v.n)} · ${fechaCorta(v.creado)}`, detalle, actual: v.actual === true };
    });
}

/**
 * «Nueva plantilla» (spec §3.1) → lo que se manda a v2/plantillas/crear_ajax y a dónde ir después.
 * tipo: 'clasica' | 'personalizada'; eleccion: el diseño de la galería o 'ia' («Empezar con IA»).
 * @returns {{error: string}|{data: {nombre, tipo, diseno_base}, sufijo: string}}
 */
export function datosNuevaPlantilla(tipo, nombre, eleccion) {
    const n = String(nombre == null ? '' : nombre).trim();
    if (tipo !== 'clasica' && tipo !== 'personalizada') return { error: 'Elige primero qué tipo de plantilla quieres.' };
    if (!/^[A-Za-z0-9_-]{4,20}$/.test(n)) {
        return { error: 'Ponle a tu plantilla un nombre de 4 a 20 caracteres: letras sin acentos, números, guion o guion bajo, sin espacios.' };
    }
    if (tipo === 'clasica') return { data: { nombre: n, tipo: 'clasica', diseno_base: 'clasico' }, sufijo: '' };
    const conIa = eleccion === 'ia';
    return { data: { nombre: n, tipo: 'personalizada', diseno_base: conIa ? 'clasico' : String(eleccion || 'clasico') }, sufijo: conIa ? '?ia=1' : '' };
}
